import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Clickable from '@v2/components/common/presentational/clickables/default';

import { page, track } from '@v2/services/analytics.service';
import Loader from '@components/common/presentational/loader/loader';
// import ProductService from '@services/product.service';
import { IProduct } from '@interfaces';
//import { useHistoryWithReferrer } from '@utils/history';
import { VarietyPackQuantityInput } from '@components/common/products/quantity';

import ProductDescription from '@components/common/products/partial/product-description';
import ProductNutrients from '@components/common/products/partial/product-nutrients';
import ProductDevelopmental from '@components/common/products/partial/product-developmental';
import ProductIngredients from '@components/common/products/partial/product-ingredients';
import ProductStandard from '@components/common/products/partial/product-standard';
import ProductUsage from '@v2/components/common/products/partial/product-safety';
import ProductFDA from '@components/common/products/partial/product-fda';
import ProductMeta from '@components/common/products/partial/product-meta';

import styles from '@components/common/products/product.styles';
import { useGTMItemDispatch } from '@v2/utils/gtm';
import { WPImage } from '@v2/utils/image';
import { apiImageSource } from '@v2/utils/http';
import { useCartAllBoxes, usePackBoxIndex } from '@app/store/cart.selectors';
import { calculateBundleTotal } from '@v2/utils/cart';
import Discontinuation from './discontinuation';
import { useShop } from '@v2/store/shop.selectors';
import ProductsRating from '@components/common/stamped/presentational/products-rating';
import { BadgesList } from '../presentational/badge/badges-list';
import ProductRatings from './partial/product-ratings';
import BundleIncludes from './partial/bundle-includes';
import BundleNeurosurgeonFounded from './partial/bundle-neurosurgeon-founded';
import BundleUsVsThem from './partial/bundle-us-vs-them';
import BundleShopOthers from './partial/bundle-shop-others';
import ArrowRightWhite from '@img/icons/arrow-right-white.svg';
import { useHistoryWithReferrer } from '@v2/utils/history';
import { useEntity } from '@v2/store/rtk.entities.selectors';
import { Animation } from '../presentational/loader/loader';


function ProductAction({ product, bundle, addAction, isMobile, readonly, parentRef, shouldShowBundle }
    : { product: IProduct, bundle: IProduct, addAction: Function, isMobile?: boolean, readonly?: boolean, parentRef?: any, shouldShowBundle?: boolean }
) {
    const categories = useEntity('categories');
    const history = useHistoryWithReferrer();
    const addToCartButtonRef = useRef(null);
    const isAdded = useRef(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const allBoxes = useCartAllBoxes();
    const packBoxIndex = usePackBoxIndex(bundle.id);

    useEffect(() => {
        const params = new URLSearchParams(history.location.search);
        if(!isAdded.current && isMobile && categories && addToCartButtonRef.current && bundle.subcategory === 'age_bundle' && params.get('add-to-cart') === 'true') {
            isAdded.current = true;
            addToCartButtonRef.current.click();
        }
    }, [bundle, categories])

    useEffect(() => {
        if (parentRef?.current && isMobile) {
            const onScroll = () => {
                const divposition = document.getElementById('top-container').getBoundingClientRect();
                const currentScrolled = divposition.top + divposition.height < 0;

                setIsScrolled(currentScrolled);
            };

            const element = document.getElementsByClassName('product-wrapper')[0].parentElement;

            if (element) {
                // clean up code
                element.removeEventListener('scroll', onScroll);
                element.addEventListener('scroll', onScroll, { passive: true });

                return () => element.removeEventListener('scroll', onScroll);
            }
        }
    }, [parentRef]);

    const bundleImage = bundle.hash ? apiImageSource(`${!bundle.veggie ? 'veggie-' : ''}bundle/${bundle.id}/${bundle.hash}/`) : product.mainImage

    return (
        <div className={`${isScrolled ? 'scrolled' : ''} ${isMobile ? styles.productBar : styles.action}`}>
            {isMobile
                ?
                <div className="bar-product-image">
                    <WPImage src={product.thumbnailImage} alt={product.thumbnailImageAlt || product.title} />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
                :
                <div className="product-image-wrapper">
                    <WPImage src={shouldShowBundle ? bundleImage : product.thumbnailImage} alt={shouldShowBundle ? bundle.title : product.thumbnailImageAlt || product.title} />
                    {product?.status === 'instock' && <Discontinuation productId={product.id} />}
                </div>
            }
            <div className='ratings-section'>
                <div className="box-ingredients">
                    <BadgesList bundle={shouldShowBundle ? bundle : null} productTitle={product.title} />
                </div>
                <div className='products-rating-container'>
                    <ProductsRating />
                </div>
            </div>
            <div className="action-wrapper">
                {!isMobile &&
                    <>
                        <div className="prices">bundle price for {bundle.size} items</div>
                        <div className="unit-price">
                            <div className="regular">
                                ${calculateBundleTotal(bundle, true).toFixed(2)}
                            </div>
                            <div className="discount">
                                ${calculateBundleTotal(bundle, false).toFixed(2)}
                            </div>
                        </div>
                        <div className="subscribe">Subscribe and Save 15%</div>
                    </>
                }
                {(bundle.status === 'instock' && addAction && !readonly) &&
                    <div className={isMobile ? 'bar-control' : 'actions'}>
                        {(allBoxes[packBoxIndex] && bundle.category === 'varietypack')
                            ?
                            <VarietyPackQuantityInput box={allBoxes[packBoxIndex]} />
                            :
                            <div className={`action add`}>
                                {!categories ? <Animation size={40} /> : <button
                                    disabled={!categories}
                                    ref={addToCartButtonRef}
                                    className="button primary red"
                                    onClick={() => addAction()}
                                    aria-label={'Add bundle to cart'}>
                                    Add {bundle.category === 'varietypack' ? 'pack' : 'bundle'} to cart
                                </button>}
                            </div>
                        }
                    </div>
                }
                {!isMobile &&
                    <div className="text-center tm-text mt-3">
                        Smart food for a developing brain and body ™
                    </div>
                }
            </div>
        </div>
    );
}

function BundlePage({ product, bundle, addAction, close, readonly, onSeeInsideClick, shouldShowBundle }
    : { product: IProduct, bundle: IProduct, addAction: Function, close: Function, readonly?: boolean, onSeeInsideClick: Function, shouldShowBundle: boolean }
) {
    const ref = useRef(null);

    return (
        <div className="product-wrapper" ref={ref}>
            {/* Commented to fix Story-5651, modal closing should be on cross button */}
            {/* <span onClick={()=> close()} style={{position: 'absolute', left: 0, top: 0, width: '100%', height: '100%'}}></span> */}
            <ProductMeta product={product} />
            <div className="left-side">
                <section className="animate__animated animate__zoomOutToIn" id="top-container">
                    <ProductAction
                        shouldShowBundle={shouldShowBundle}
                        product={product}
                        bundle={bundle}
                        addAction={addAction}
                        readonly={readonly} />
                </section>
            </div>
            <div className="right-side">
                <section>
                    <BundleIncludes bundle={bundle} />
                </section>
                <div className={styles.buttonContainer}>
                    <Clickable onClick={onSeeInsideClick} className={`${styles.showInsideButton} secondary`}>
                        <div>see inside this bundle</div>
                        <img src={ArrowRightWhite} alt=''/>
                    </Clickable>
                </div>
                <section>
                    <BundleNeurosurgeonFounded bundle={bundle} />
                </section>
                <section>
                    <BundleUsVsThem bundle={bundle} />
                </section>
                <section>
                    <ProductRatings />
                </section>
                <section className='bundle-shop-others'>
                    <BundleShopOthers bundle={bundle} />
                </section>
                <ProductAction
                    shouldShowBundle={shouldShowBundle}
                    product={product}
                    bundle={bundle}
                    addAction={addAction}
                    isMobile={true}
                    parentRef={ref} />
                <div style={{ height: 50 }}></div>
            </div>
        </div>
    );
}

function ProductPage({ product, bundle, addAction, close, readonly }
    : { product: IProduct, bundle: IProduct, addAction: Function, close: Function, readonly?: boolean }
) {
    const ref = useRef(null);

    return (
        <div className="product-wrapper" ref={ref}>
            {/* Commented to fix Story-5651, modal closing should be on cross button */}
            {/* <span onClick={()=> close()} style={{position: 'absolute', left: 0, top: 0, width: '100%', height: '100%'}}></span> */}
            <ProductMeta product={product} />
            <div className="left-side">
                <section className="animate__animated animate__zoomOutToIn" id="top-container">
                    <ProductAction
                        product={product}
                        bundle={bundle}
                        addAction={addAction}
                        readonly={readonly} />
                </section>
            </div>

            <div className="right-side">
                <section>
                    <ProductDescription product={product} />
                </section>
                <section>
                    <ProductFDA product={product} />
                </section>
                <section>
                    <ProductIngredients product={product} />
                </section>
                <section>
                    <ProductNutrients product={product} />
                </section>
                <section className="developmental-section">
                    <ProductDevelopmental product={product} />
                </section>
                <section className="standard-section">
                    <ProductStandard product={product} />
                </section>
                <section className="safety-usage-section">
                    <ProductUsage product={product} />
                </section>
                <section>
                    <ProductRatings />
                </section>
                <ProductAction
                    product={product}
                    bundle={bundle}
                    addAction={addAction}
                    isMobile={true}
                    parentRef={ref} />
                <div style={{ height: 50 }}></div>
            </div>
        </div>
    );
}

export function BundleModalBody({ bundle, addAction, close, readonly }: { bundle: IProduct, addAction: Function, close: Function, readonly?: boolean }) {
    const location = useLocation();
    const gtmDispatch = useGTMItemDispatch();
    const productsBundle = useShop().veganOnly && bundle.veggie ? bundle.veggie.products : bundle.products;
    const [index, setIndex] = useState(0);
    const selectedBundle = useShop().veganOnly ? { ...bundle, ...bundle.veggie } : bundle;
    const [isShowBundleProducts, setIsShowBundleProducts] = useState(false);
    const [product, setProduct] = useState(productsBundle && productsBundle[index] ? productsBundle[index] : null);
    const countProducts = Object.keys(productsBundle).length;

    const nextProductAction = () => index < countProducts - 1 ? setIndex(index + 1) : {};
    const prevProductAction = () => index > 0 ? setIndex(index - 1) : {}

    const handleSeeInside = () => {
        document.getElementById('bundle-modal-body-id').scrollTo({ top: 0, behavior: 'smooth' })
        setIsShowBundleProducts(true);
    }

    const handleHideSeeInside = () => {
        document.getElementById('bundle-modal-body-id').scrollTo({ top: 0, behavior: 'smooth' })
        setIsShowBundleProducts(false);
    }

    useEffect(() => {
        if (productsBundle[index] && productsBundle[index].id) {
            setProduct(productsBundle[index]);

        }
    }, [index]);

    useEffect(() => {
        if (!bundle || !bundle.id) {
            return null;
        }

        const price = calculateBundleTotal(bundle);

        page('Bundle', {
            url: window.location.href,
            path: window.location.pathname,
            cbReferrer: (location && location.state) ? (location.state as any).referrer : document.referrer,
            content_category: bundle.category,
            content_ids: [],
            content_name: bundle.title,
            content_type: bundle.category,
        });

        // This one is used by FB
        track('PDP Viewed', {
            id: bundle.id,
            content_category: bundle.category,
            content_ids: productsBundle.map(product => product.sku),
            content_name: bundle.title,
            content_type: 'product',
            product_availability: bundle.status === 'instock' ? 'in stock' : 'out of stock',
            availability: bundle.status === 'instock' ? 'in stock' : 'out of stock',
            title: bundle.title,
            link: `https://cerebelly.com/bundle/${bundle.subcategory === 'age_bundle' ? 'age' : 'variety'}/${bundle.slug}`,
            image_link: bundle.hash ? apiImageSource(`bundle/${bundle.id}/${bundle.hash}/`) : bundle.mainImage,
            price: `${price} USD`,
            google_product_category: 5719,
            offers: {
                price: price,
                priceCurrency: 'USD',
                availability: product?.status === 'instock' ? 'in stock' : 'out of stock',
            }
        });

        const value = typeof price === 'number' ? price : parseFloat(price);
        gtmDispatch('view_item', bundle, { value: parseFloat(value.toFixed(2)) });
    }, []);

    const BUNDLE_PREVIEW_CATEGORIES = ['age_bundle', 'variety_pack'];
    const shouldShowBundle = !(isShowBundleProducts || (!isShowBundleProducts && !BUNDLE_PREVIEW_CATEGORIES.includes(bundle.subcategory)))

    return (
        <div className={styles.overlay}
            tabIndex={-1}
            role="dialog" id="bundle-modal-body-id">
            {selectedBundle ? <>
                {!shouldShowBundle ?
                    <>
                        {index > 0 &&
                            <Clickable className="prev" onClick={prevProductAction}></Clickable>
                        }
                        <ProductPage product={product} readonly={readonly} bundle={selectedBundle as IProduct} close={close} addAction={addAction} />
                        {index < countProducts - 1 &&
                            <Clickable className="next" onClick={nextProductAction}></Clickable>
                        }
                        {index === 0 &&
                            <Clickable className={`${styles.showInsideButton} primary top`} onClick={handleHideSeeInside}>
                                <img src={ArrowRightWhite} alt=''/>
                            </Clickable>
                        }
                    </> :
                    <>
                        <Clickable className={`${styles.showInsideButton} primary`} onClick={handleSeeInside}>
                            <img src={ArrowRightWhite} alt=''/>
                            <div>see inside this bundle</div>
                        </Clickable>
                        <BundlePage
                            shouldShowBundle={shouldShowBundle}
                            onSeeInsideClick={handleSeeInside}
                            product={product} readonly={readonly}
                            bundle={selectedBundle as IProduct} close={close}
                            addAction={addAction}
                        />
                    </>
                }
            </> : <Loader />}
        </div>
    );
}

export default function BundleModal({ bundle: bundlePromise, addAction, hide }: { bundle: Promise<IProduct>, addAction: Function, hide: (() => void)}) {
    const [bundle, setBundle] = useState(null);

    useEffect(() => {
        bundlePromise.then(setBundle);
    }, []);

    return bundle ? <BundleModalBody bundle={bundle} addAction={addAction} close={hide} /> : null;
}